import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { portalAuthGuard } from '@eternium/feature-portal/auth/portal-auth.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },
      // ══════ ETERNIUM — grupo Cementerio (M01 Maestros y Configuración) ══════
      // Las URLs top-level coinciden con las sembradas en BD por EterniumModuleInitializer: /cementerio/*
      {
        path: 'cementerio',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'configuracion-general',
            pathMatch: 'full',
          },
          {
            path: 'configuracion-general',
            loadComponent: () => import('@eternium/feature-configuracion/configuracion-general/configuracion-general.component').then(m => m.ConfiguracionGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnConfigGeneral',
          },
          {
            path: 'sedes',
            loadComponent: () => import('@eternium/feature-configuracion/sedes/sedes.component').then(m => m.SedesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnSedes',
          },
          {
            path: 'series',
            loadComponent: () => import('@eternium/feature-configuracion/series/series.component').then(m => m.SeriesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnSeries',
          },
          {
            path: 'tipos-espacio',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/tipos-espacio/tipos-espacio.component').then(m => m.TiposEspacioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTiposEspacio',
          },
          {
            path: 'pabellones',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/pabellones/pabellones.component').then(m => m.PabellonesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPabellones',
          },
          {
            path: 'tarifas',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/tarifas/tarifas.component').then(m => m.TarifasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTarifas',
          },
          {
            path: 'servicios',
            loadComponent: () => import('@eternium/feature-servicios/servicios/servicios.component').then(m => m.ServiciosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnServicios',
          },
          {
            path: 'paquetes',
            loadComponent: () => import('@eternium/feature-servicios/paquetes/paquetes.component').then(m => m.PaquetesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPaquetes',
          },
          {
            path: 'plantillas',
            loadComponent: () => import('@eternium/feature-plantillas/plantillas/plantillas.component').then(m => m.PlantillasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlantillas',
          },
          {
            path: 'plantillas/editor/:id',
            loadComponent: () => import('@eternium/feature-plantillas/plantillas/components/plantilla-editor/plantilla-editor.component').then(m => m.PlantillaEditorComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.etnPlantillaEditor',
          },
        ],
      },
      // ══════ ETERNIUM — vista de sistema (grupo "Cementerio Sistema") ══════
      // Espejo del bloque anterior con los MISMOS componentes: el sidebar del Provider solo
      // muestra grupos system=1, así que los módulos necesitan estas rutas paralelas
      // (mismo patrón que Centrix con /flujos-sistema/*). URLs sembradas por
      // EterniumModuleInitializer: /cementerio-sistema/*
      {
        path: 'cementerio-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'configuracion-general',
            pathMatch: 'full',
          },
          {
            path: 'configuracion-general',
            loadComponent: () => import('@eternium/feature-configuracion/configuracion-general/configuracion-general.component').then(m => m.ConfiguracionGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnConfigGeneral',
          },
          {
            path: 'sedes',
            loadComponent: () => import('@eternium/feature-configuracion/sedes/sedes.component').then(m => m.SedesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnSedes',
          },
          {
            path: 'series',
            loadComponent: () => import('@eternium/feature-configuracion/series/series.component').then(m => m.SeriesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnSeries',
          },
          {
            path: 'tipos-espacio',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/tipos-espacio/tipos-espacio.component').then(m => m.TiposEspacioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTiposEspacio',
          },
          {
            path: 'pabellones',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/pabellones/pabellones.component').then(m => m.PabellonesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPabellones',
          },
          {
            path: 'tarifas',
            loadComponent: () => import('@eternium/feature-catalogo-espacios/tarifas/tarifas.component').then(m => m.TarifasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTarifas',
          },
          {
            path: 'servicios',
            loadComponent: () => import('@eternium/feature-servicios/servicios/servicios.component').then(m => m.ServiciosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnServicios',
          },
          {
            path: 'paquetes',
            loadComponent: () => import('@eternium/feature-servicios/paquetes/paquetes.component').then(m => m.PaquetesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPaquetes',
          },
          {
            path: 'plantillas',
            loadComponent: () => import('@eternium/feature-plantillas/plantillas/plantillas.component').then(m => m.PlantillasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlantillas',
          },
          {
            path: 'plantillas/editor/:id',
            loadComponent: () => import('@eternium/feature-plantillas/plantillas/components/plantilla-editor/plantilla-editor.component').then(m => m.PlantillaEditorComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.etnPlantillaEditor',
          },
        ],
      },
      // ══════ ETERNIUM — categoría Espacios (M02 Gestión de Espacios Funerarios) ══════
      // URLs sembradas por EterniumModuleInitializer: /cementerio-espacios/*
      {
        path: 'cementerio-espacios',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'inventario',
            pathMatch: 'full',
          },
          {
            path: 'inventario',
            loadComponent: () => import('@eternium/feature-espacios/inventario/inventario.component').then(m => m.InventarioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnInventario',
          },
          {
            path: 'disponibilidad',
            loadComponent: () => import('@eternium/feature-espacios/disponibilidad/disponibilidad.component').then(m => m.DisponibilidadComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDisponibilidad',
          },
          {
            path: 'plano',
            loadComponent: () => import('@eternium/feature-espacios/plano/plano.component').then(m => m.PlanoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlano',
          },
          {
            path: 'historial',
            loadComponent: () => import('@eternium/feature-espacios/historial/historial.component').then(m => m.HistorialComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnHistorialEspacios',
          },
        ],
      },
      // Espejo de plataforma (system=1) — mismos componentes, otras rutas.
      {
        path: 'cementerio-espacios-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'inventario',
            pathMatch: 'full',
          },
          {
            path: 'inventario',
            loadComponent: () => import('@eternium/feature-espacios/inventario/inventario.component').then(m => m.InventarioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnInventario',
          },
          {
            path: 'disponibilidad',
            loadComponent: () => import('@eternium/feature-espacios/disponibilidad/disponibilidad.component').then(m => m.DisponibilidadComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDisponibilidad',
          },
          {
            path: 'plano',
            loadComponent: () => import('@eternium/feature-espacios/plano/plano.component').then(m => m.PlanoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlano',
          },
          {
            path: 'historial',
            loadComponent: () => import('@eternium/feature-espacios/historial/historial.component').then(m => m.HistorialComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnHistorialEspacios',
          },
        ],
      },
      // ══════ ETERNIUM — categoría Contratos (M03 Contratos y Titularidades) ══════
      // URLs sembradas por EterniumModuleInitializer: /cementerio-contratos/*
      {
        path: 'cementerio-contratos',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'titulares',
            pathMatch: 'full',
          },
          {
            path: 'titulares',
            loadComponent: () => import('@eternium/feature-contratos/titulares/titulares.component').then(m => m.TitularesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTitulares',
          },
          {
            path: 'contratos',
            loadComponent: () => import('@eternium/feature-contratos/contratos/contratos.component').then(m => m.ContratosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnContratos',
          },
          {
            path: 'documentos',
            loadComponent: () => import('@eternium/feature-contratos/documentos/documentos.component').then(m => m.DocumentosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDocumentos',
          },
          {
            path: 'transferencias',
            loadComponent: () => import('@eternium/feature-contratos/transferencias/transferencias.component').then(m => m.TransferenciasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTransferencias',
          },
        ],
      },
      // Espejo de plataforma (system=1) — mismos componentes, otras rutas.
      {
        path: 'cementerio-contratos-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'titulares',
            pathMatch: 'full',
          },
          {
            path: 'titulares',
            loadComponent: () => import('@eternium/feature-contratos/titulares/titulares.component').then(m => m.TitularesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTitulares',
          },
          {
            path: 'contratos',
            loadComponent: () => import('@eternium/feature-contratos/contratos/contratos.component').then(m => m.ContratosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnContratos',
          },
          {
            path: 'documentos',
            loadComponent: () => import('@eternium/feature-contratos/documentos/documentos.component').then(m => m.DocumentosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDocumentos',
          },
          {
            path: 'transferencias',
            loadComponent: () => import('@eternium/feature-contratos/transferencias/transferencias.component').then(m => m.TransferenciasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnTransferencias',
          },
        ],
      },
      // ══════ ETERNIUM — categoría Ventas (M04 Ventas y Reservas) ══════
      // URLs sembradas por EterniumModuleInitializer: /cementerio-ventas/*
      {
        path: 'cementerio-ventas',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'prospectos',
            pathMatch: 'full',
          },
          {
            path: 'prospectos',
            loadComponent: () => import('@eternium/feature-ventas/prospectos/prospectos.component').then(m => m.ProspectosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnProspectos',
          },
          {
            path: 'cotizaciones',
            loadComponent: () => import('@eternium/feature-ventas/cotizaciones/cotizaciones.component').then(m => m.CotizacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCotizaciones',
          },
          {
            path: 'reservas',
            loadComponent: () => import('@eternium/feature-ventas/reservas/reservas.component').then(m => m.ReservasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReservas',
          },
          {
            path: 'ventas',
            loadComponent: () => import('@eternium/feature-ventas/ventas/ventas.component').then(m => m.VentasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnVentas',
          },
          {
            path: 'pipeline',
            loadComponent: () => import('@eternium/feature-ventas/pipeline/pipeline.component').then(m => m.PipelineComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPipeline',
          },
        ],
      },
      // Espejo de plataforma (system=1) — mismos componentes, otras rutas.
      {
        path: 'cementerio-ventas-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'prospectos',
            pathMatch: 'full',
          },
          {
            path: 'prospectos',
            loadComponent: () => import('@eternium/feature-ventas/prospectos/prospectos.component').then(m => m.ProspectosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnProspectos',
          },
          {
            path: 'cotizaciones',
            loadComponent: () => import('@eternium/feature-ventas/cotizaciones/cotizaciones.component').then(m => m.CotizacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCotizaciones',
          },
          {
            path: 'reservas',
            loadComponent: () => import('@eternium/feature-ventas/reservas/reservas.component').then(m => m.ReservasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReservas',
          },
          {
            path: 'ventas',
            loadComponent: () => import('@eternium/feature-ventas/ventas/ventas.component').then(m => m.VentasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnVentas',
          },
          {
            path: 'pipeline',
            loadComponent: () => import('@eternium/feature-ventas/pipeline/pipeline.component').then(m => m.PipelineComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPipeline',
          },
        ],
      },
      // ══════ ETERNIUM — categoría Pagos (M06 Fase 5a: planes de pago y caja) ══════
      // URLs sembradas por EterniumModuleInitializer: /cementerio-pagos/*
      {
        path: 'cementerio-pagos',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'planes-pago',
            pathMatch: 'full',
          },
          {
            path: 'planes-pago',
            loadComponent: () => import('@eternium/feature-pagos/planes-pago/planes-pago.component').then(m => m.PlanesPagoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlanesPago',
          },
          {
            path: 'caja',
            loadComponent: () => import('@eternium/feature-pagos/caja/caja.component').then(m => m.CajaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCaja',
          },
          {
            path: 'facturacion',
            loadComponent: () => import('@eternium/feature-pagos/facturacion/facturacion.component').then(m => m.FacturacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnFacturacion',
          },
          {
            path: 'cobranza',
            loadComponent: () => import('@eternium/feature-pagos/cobranza/cobranza.component').then(m => m.CobranzaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCobranza',
          },
          {
            path: 'conciliacion',
            loadComponent: () => import('@eternium/feature-pagos/conciliacion/conciliacion.component').then(m => m.ConciliacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnConciliacion',
          },
          {
            path: 'reportes',
            loadComponent: () => import('@eternium/feature-pagos/reportes/reportes.component').then(m => m.ReportesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReportesFin',
          },
        ],
      },
      {
        path: 'cementerio-pagos-sistema',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'planes-pago',
            pathMatch: 'full',
          },
          {
            path: 'planes-pago',
            loadComponent: () => import('@eternium/feature-pagos/planes-pago/planes-pago.component').then(m => m.PlanesPagoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPlanesPago',
          },
          {
            path: 'caja',
            loadComponent: () => import('@eternium/feature-pagos/caja/caja.component').then(m => m.CajaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCaja',
          },
          {
            path: 'facturacion',
            loadComponent: () => import('@eternium/feature-pagos/facturacion/facturacion.component').then(m => m.FacturacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnFacturacion',
          },
          {
            path: 'cobranza',
            loadComponent: () => import('@eternium/feature-pagos/cobranza/cobranza.component').then(m => m.CobranzaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCobranza',
          },
          {
            path: 'conciliacion',
            loadComponent: () => import('@eternium/feature-pagos/conciliacion/conciliacion.component').then(m => m.ConciliacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnConciliacion',
          },
          {
            path: 'reportes',
            loadComponent: () => import('@eternium/feature-pagos/reportes/reportes.component').then(m => m.ReportesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReportesFin',
          },
        ],
      },
      // ══════ ETERNIUM — categoría Servicios Funerarios (M05 Fase 6a) ══════
      // URLs sembradas por EterniumModuleInitializer: /cementerio-servicios/*
      {
        path: 'cementerio-servicios',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'difuntos',
            pathMatch: 'full',
          },
          {
            path: 'difuntos',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/difuntos/difuntos.component').then(m => m.DifuntosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDifuntos',
          },
          {
            path: 'ordenes-servicio',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/ordenes-servicio/ordenes-servicio.component').then(m => m.OrdenesServicioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnOrdenesServicio',
          },
          {
            path: 'inhumaciones',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/inhumaciones/inhumaciones.component').then(m => m.InhumacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnInhumaciones',
          },
          {
            path: 'cremaciones',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/cremaciones/cremaciones.component').then(m => m.CremacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCremaciones',
          },
          {
            path: 'permisos-municipales',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/permisos-municipales/permisos-municipales.component').then(m => m.PermisosMunicipalesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPermisosMunicipales',
          },
          {
            path: 'agenda',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/agenda/agenda.component').then(m => m.AgendaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnAgenda',
          },
          {
            path: 'capillas',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/capillas/capillas.component').then(m => m.CapillasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCapillas',
          },
        ],
      },
      {
        path: 'cementerio-servicios-sistema',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'difuntos',
            pathMatch: 'full',
          },
          {
            path: 'difuntos',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/difuntos/difuntos.component').then(m => m.DifuntosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDifuntos',
          },
          {
            path: 'ordenes-servicio',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/ordenes-servicio/ordenes-servicio.component').then(m => m.OrdenesServicioComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnOrdenesServicio',
          },
          {
            path: 'inhumaciones',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/inhumaciones/inhumaciones.component').then(m => m.InhumacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnInhumaciones',
          },
          {
            path: 'cremaciones',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/cremaciones/cremaciones.component').then(m => m.CremacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCremaciones',
          },
          {
            path: 'permisos-municipales',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/permisos-municipales/permisos-municipales.component').then(m => m.PermisosMunicipalesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPermisosMunicipales',
          },
          {
            path: 'agenda',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/agenda/agenda.component').then(m => m.AgendaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnAgenda',
          },
          {
            path: 'capillas',
            loadComponent: () => import('@eternium/feature-servicios-funerarios/capillas/capillas.component').then(m => m.CapillasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnCapillas',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-mantenimiento/*  (Fase 7 — M07 Mantenimiento)
      {
        path: 'cementerio-mantenimiento',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'programacion',
            pathMatch: 'full',
          },
          {
            path: 'programacion',
            loadComponent: () => import('@eternium/feature-mantenimiento/programacion/programacion.component').then(m => m.ProgramacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoProgramacion',
          },
          {
            path: 'ejecucion',
            loadComponent: () => import('@eternium/feature-mantenimiento/ejecucion/ejecucion.component').then(m => m.EjecucionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoEjecucion',
          },
          {
            path: 'supervision',
            loadComponent: () => import('@eternium/feature-mantenimiento/supervision/supervision.component').then(m => m.SupervisionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoSupervision',
          },
          {
            path: 'infraestructura',
            loadComponent: () => import('@eternium/feature-mantenimiento/infraestructura/infraestructura.component').then(m => m.InfraestructuraComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoInfraestructura',
          },
          {
            path: 'configuracion-mantenimiento',
            loadComponent: () => import('@eternium/feature-mantenimiento/configuracion/configuracion-mantenimiento.component').then(m => m.ConfiguracionMantenimientoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoConfiguracion',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-mantenimiento-sistema/*  (Fase 7 — M07 Mantenimiento)
      {
        path: 'cementerio-mantenimiento-sistema',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'programacion',
            pathMatch: 'full',
          },
          {
            path: 'programacion',
            loadComponent: () => import('@eternium/feature-mantenimiento/programacion/programacion.component').then(m => m.ProgramacionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoProgramacion',
          },
          {
            path: 'ejecucion',
            loadComponent: () => import('@eternium/feature-mantenimiento/ejecucion/ejecucion.component').then(m => m.EjecucionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoEjecucion',
          },
          {
            path: 'supervision',
            loadComponent: () => import('@eternium/feature-mantenimiento/supervision/supervision.component').then(m => m.SupervisionComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoSupervision',
          },
          {
            path: 'infraestructura',
            loadComponent: () => import('@eternium/feature-mantenimiento/infraestructura/infraestructura.component').then(m => m.InfraestructuraComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoInfraestructura',
          },
          {
            path: 'configuracion-mantenimiento',
            loadComponent: () => import('@eternium/feature-mantenimiento/configuracion/configuracion-mantenimiento.component').then(m => m.ConfiguracionMantenimientoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnMtoConfiguracion',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-portal/*  (Fase 8 — M08 back-office del portal)
      {
        path: 'cementerio-portal',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'solicitudes', pathMatch: 'full' },
          {
            path: 'solicitudes',
            loadComponent: () => import('@eternium/feature-atencion/solicitudes/solicitudes.component').then(m => m.SolicitudesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalSolicitudes',
          },
          {
            path: 'cuentas',
            loadComponent: () => import('@eternium/feature-atencion/cuentas/cuentas.component').then(m => m.CuentasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalCuentas',
          },
          {
            path: 'tipos-solicitud',
            loadComponent: () => import('@eternium/feature-atencion/tipos-solicitud/tipos-solicitud.component').then(m => m.TiposSolicitudComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalTipos',
          },
          {
            path: 'pasarelas',
            loadComponent: () => import('@eternium/feature-atencion/pasarelas/pasarelas.component').then(m => m.PasarelasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalPasarelas',
          },
          {
            path: 'consentimientos',
            loadComponent: () => import('@eternium/feature-atencion/consentimientos/consentimientos.component').then(m => m.ConsentimientosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalConsentimientos',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-portal-sistema/*  (Fase 8 — M08 back-office del portal)
      {
        path: 'cementerio-portal-sistema',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'solicitudes', pathMatch: 'full' },
          {
            path: 'solicitudes',
            loadComponent: () => import('@eternium/feature-atencion/solicitudes/solicitudes.component').then(m => m.SolicitudesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalSolicitudes',
          },
          {
            path: 'cuentas',
            loadComponent: () => import('@eternium/feature-atencion/cuentas/cuentas.component').then(m => m.CuentasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalCuentas',
          },
          {
            path: 'tipos-solicitud',
            loadComponent: () => import('@eternium/feature-atencion/tipos-solicitud/tipos-solicitud.component').then(m => m.TiposSolicitudComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalTipos',
          },
          {
            path: 'pasarelas',
            loadComponent: () => import('@eternium/feature-atencion/pasarelas/pasarelas.component').then(m => m.PasarelasComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalPasarelas',
          },
          {
            path: 'consentimientos',
            loadComponent: () => import('@eternium/feature-atencion/consentimientos/consentimientos.component').then(m => m.ConsentimientosComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnPortalConsentimientos',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-analitica/*  (Fase 9 — M10 Dashboard y Reportería)
      {
        path: 'cementerio-analitica',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'dashboard-ejecutivo', pathMatch: 'full' },
          {
            path: 'dashboard-ejecutivo',
            loadComponent: () => import('@eternium/feature-analitica/dashboard/dashboard-ejecutivo.component').then(m => m.DashboardEjecutivoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDashboardEjecutivo',
          },
          {
            path: 'reportes-operativos',
            loadComponent: () => import('@eternium/feature-analitica/reportes/reportes.component').then(m => m.ReportesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReportesOperativos',
          },
          {
            path: 'auditoria',
            loadComponent: () => import('@eternium/feature-analitica/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnAuditoria',
          },
        ],
      },
      // URLs sembradas por EterniumModuleInitializer: /cementerio-analitica-sistema/*  (Fase 9 — M10 Dashboard y Reportería)
      {
        path: 'cementerio-analitica-sistema',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'dashboard-ejecutivo', pathMatch: 'full' },
          {
            path: 'dashboard-ejecutivo',
            loadComponent: () => import('@eternium/feature-analitica/dashboard/dashboard-ejecutivo.component').then(m => m.DashboardEjecutivoComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnDashboardEjecutivo',
          },
          {
            path: 'reportes-operativos',
            loadComponent: () => import('@eternium/feature-analitica/reportes/reportes.component').then(m => m.ReportesComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnReportesOperativos',
          },
          {
            path: 'auditoria',
            loadComponent: () => import('@eternium/feature-analitica/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            title: 'titles.etnAuditoria',
          },
        ],
      },
    ],
  },
  // Portal de familias (Fase 8 — M08): zona PÚBLICA con identidad propia (portal_accounts), sin AppLayout.
  {
    path: 'portal/:empresa/acceso',
    loadComponent: () => import('@eternium/feature-portal/acceso/portal-acceso.component').then(m => m.PortalAccesoComponent),
    title: 'titles.etnPortalAcceso',
  },
  {
    path: 'portal/:empresa',
    loadComponent: () => import('@eternium/feature-portal/layout/portal-layout.component').then(m => m.PortalLayoutComponent),
    canActivate: [portalAuthGuard],
    children: [
      { path: '', redirectTo: 'inicio', pathMatch: 'full' },
      { path: 'inicio', loadComponent: () => import('@eternium/feature-portal/inicio/portal-inicio.component').then(m => m.PortalInicioComponent), title: 'titles.etnPortalInicio' },
      { path: 'espacios', loadComponent: () => import('@eternium/feature-portal/espacios/portal-espacios.component').then(m => m.PortalEspaciosComponent), title: 'titles.etnPortalEspacios' },
      { path: 'espacios/:spaceId', loadComponent: () => import('@eternium/feature-portal/espacios/portal-espacio-detalle.component').then(m => m.PortalEspacioDetalleComponent), title: 'titles.etnPortalEspacios' },
      { path: 'pagos', loadComponent: () => import('@eternium/feature-portal/pagos/portal-pagos.component').then(m => m.PortalPagosComponent), title: 'titles.etnPortalPagos' },
      { path: 'pagos/confirmacion/:transactionId', loadComponent: () => import('@eternium/feature-portal/pagos/portal-pago-confirmacion.component').then(m => m.PortalPagoConfirmacionComponent), title: 'titles.etnPortalPagos' },
      { path: 'documentos', loadComponent: () => import('@eternium/feature-portal/documentos/portal-documentos.component').then(m => m.PortalDocumentosComponent), title: 'titles.etnPortalDocumentos' },
      { path: 'solicitudes', loadComponent: () => import('@eternium/feature-portal/solicitudes/portal-solicitudes.component').then(m => m.PortalSolicitudesComponent), title: 'titles.etnPortalSolicitudesTitular' },
      { path: 'solicitudes/nueva', loadComponent: () => import('@eternium/feature-portal/solicitudes/portal-solicitud-nueva.component').then(m => m.PortalSolicitudNuevaComponent), title: 'titles.etnPortalSolicitudesTitular' },
      { path: 'solicitudes/:requestId', loadComponent: () => import('@eternium/feature-portal/solicitudes/portal-solicitud-detalle.component').then(m => m.PortalSolicitudDetalleComponent), title: 'titles.etnPortalSolicitudesTitular' },
      { path: 'cuenta', loadComponent: () => import('@eternium/feature-portal/cuenta/portal-cuenta.component').then(m => m.PortalCuentaComponent), title: 'titles.etnPortalCuenta' },
    ],
  },
  { path: 'portal', redirectTo: 'portal/_/acceso', pathMatch: 'full' },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];

